import {
  alpha,
  Box,
  Card,
  Stack,
  styled,
  Text,
} from '@ltvco/refresh-lib/theme';
import { Check } from '@mui/icons-material';
import { useEffect, useState } from 'react';

import { constants } from 'appConstants';

const DeviceLoaderImage = styled('img')(({ theme }) => ({
  width: 120,
  height: 120,
  marginBottom: theme.spacing(3),
  display: 'block',
}));

const STEPS = [
  'Validating device ID format',
  'Querying GSMA Block List',
  'Querying GSMA General List',
  'Resolving TAC → device type details',
] as const;

type StepState = 'pending' | 'active' | 'done';

export interface DeviceCheckLoadingProps {
  deviceId: string;
  isLoading: boolean;
}

export default function DeviceCheckLoading({
  deviceId,
  isLoading,
}: DeviceCheckLoadingProps) {
  const { images } = constants;
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    setCurrentStep(0);

    if (!isLoading) {
      setCurrentStep(STEPS.length);
      return;
    }

    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev >= STEPS.length - 1) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 600);

    return () => clearInterval(interval);
  }, [isLoading]);

  const allDone = currentStep >= STEPS.length;

  const getState = (index: number): StepState => {
    if (currentStep >= STEPS.length) {
      return 'done';
    }
    if (index < currentStep) {
      return 'done';
    }
    if (index === currentStep) {
      return 'active';
    }
    return 'pending';
  };

  return (
    <Card
      variant="outlined"
      sx={{
        maxWidth: 380,
        mx: 'auto',
        borderRadius: 2.5,
        p: { xs: 3, sm: '2.5rem 2rem 2rem' },
      }}
    >
      <Stack alignItems="center">
        <DeviceLoaderImage src={images.deviceLoaderGif} alt="" />

        <Text variant="h5" sx={{ mb: 0.75, fontWeight: 600 }}>
          {allDone ? 'Device verified' : 'Checking your device'}
        </Text>

        <Text
          variant="body1"
          sx={{
            letterSpacing: '0.05em',
            color: 'text.secondary',
            mb: 3.5,
          }}
        >
          IMEI {deviceId}
        </Text>

        <Stack width="100%" spacing={1.75}>
          {STEPS.map((label, index) => {
            const state = getState(index);
            return (
              <Stack
                key={label}
                direction="row"
                alignItems="center"
                spacing={1.5}
              >
                <StepIcon state={state} />
                <Text
                  variant="body2"
                  sx={{
                    color:
                      state === 'active'
                        ? 'text.primary'
                        : state === 'done'
                        ? 'text.secondary'
                        : 'text.disabled',
                    transition: 'color 0.3s',
                  }}
                >
                  {label}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}

function StepIcon({ state }: { state: StepState }) {
  const iconSize = 24;

  if (state === 'done') {
    return (
      <Box
        sx={{
          width: iconSize,
          height: iconSize,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'success.main',
          transition: 'background-color 0.3s',
        }}
      >
        <Check sx={{ fontSize: 13, color: 'success.contrastText' }} />
      </Box>
    );
  }

  if (state === 'active') {
    return (
      <Box
        sx={(theme) => ({
          width: iconSize,
          height: iconSize,
          borderRadius: '50%',
          flexShrink: 0,
          border: 2,
          borderColor: 'primary.main',
          bgcolor: 'transparent',
          transition: 'border-color 0.3s',
          animation: 'pulseBorder 1s ease-in-out infinite',
          '@keyframes pulseBorder': {
            '0%, 100%': {
              boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '50%': {
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          },
        })}
      />
    );
  }

  return (
    <Box
      sx={{
        width: iconSize,
        height: iconSize,
        borderRadius: '50%',
        flexShrink: 0,
        border: 1.5,
        borderColor: 'divider',
        bgcolor: 'transparent',
        transition: 'border-color 0.3s',
      }}
    />
  );
}
